import {
  PublicEWebinarFragment,
  UtmParamsFragment,
  RegistrationFormField,
  CookieBannerMode,
  AttendeeField,
  UngatedSessionType,
} from '../../GraphQL/schema/types';
import { V1VisitorResponse } from '../../shared/frontend/v1APITypes';
import { ServerDateNow } from '../../utils/serverDate';

enum SessionType {
  JustInTime = 'JustInTime',
  Replay = 'Replay',
  Scheduled = 'Scheduled',
}
type Session = import('../../GraphQL/schema/types').Session;
type AttendeeUrls = import('../../GraphQL/schema/types').AttendeeUrls;
type ProductHuntPromoInteractionDetails =
  import('../../GraphQL/schema/types').ProductHuntPromoInteractionDetails;
export { Session, SessionType, AttendeeUrls };

export enum WidgetFor {
  Registration = 'Registration',
  Thankyou = 'Thankyou',
}

export enum RegistrationFormType {
  FastRendering = 'FastRendering',
  LatestForm = 'LatestForm',
}
export interface Presenter {
  name: string;
  avatar?: string;
}

export enum WidgetType {
  FloatIn = 'FloatIn',
  Button = 'Button',
  Card = 'Card',
  ThankYou = 'ThankYou',
  FixedBar = 'FixedBar',
  RegForm = 'RegForm',
  EwebinarRegForm = 'EwebinarRegForm',
  GdprBanner = 'GdprBanner',
  PresenterCarousel = 'PresenterCarousel',
  EmbeddedPlayer = 'EmbeddedPlayer',
}

export enum WidgetPosition {
  Left = 'Left',
  Center = 'Center',
  Right = 'Right',
  Hidden = 'Hidden',
}

export type WidgetTask = [string, WidgetConfig, boolean];

export interface Widget {
  resize: (dimensions: { width: number; height: number }) => void;
  render: (dom: HTMLElement) => void;
}

export interface RegisterData {
  id: string;
  joinLink?: string;
}

export interface XProps {
  on: (listener: WidgetEventListener) => void;
  emit: <T extends WidgetEventType>(event: Extract<WidgetEvent, { event: T }>) => void;
}

export interface Dimensions {
  width: number;
  height: number;
}

export type IntegrationDetails = ProductHuntPromoInteractionDetails;

export interface WidgetInfo {
  parentUrl?: string;
  width?: number;
  height?: number;
  params?: Record<string, string>;
  source?: string;
  referrer?: string;
  visitorId?: string;
  visitor?: V1VisitorResponse;
}

export enum PageMode {
  preview = 'preview',
  edit = 'edit',
  public = 'public',
}

export enum FormSessionType {
  ShowDropdown = 'ShowDropdown',
  OnDemand = 'OnDemand',
  Replay = 'Replay',
  FirstAvailable = 'FirstAvailable',
}

export interface LegacyWidgetConfig {
  mode?: PageMode;
  root?: string;
  url: string;
  shortUrl: string;
  btnText: string;
  isReview?: boolean;
  collapsed?: boolean;
  source?: string;
  onlyButton?: boolean;
  jitBtnText?: string;
  replayBtnText?: string;
  addToCalendarBtnText?: string;
  title: string;
  description: string;
  image: string;
  video?: string;
  primaryColor: string;
  readableColor: string;
  buttonPrimaryColor?: string;
  buttonReadableColor?: string;
  readableOnWhiteColor: string;
  language: string;
  borderRadius: number;
  openInPopup?: boolean;
  showButtonTimer?: boolean;
  buttonTextPortalId?: string;
  for?: WidgetFor;
  type: WidgetType;
  position: WidgetPosition;
  mobilePosition: WidgetPosition;
  presenters?: Presenter[];
  thankYouData?: ThankYouData;
  sessions: Session[];
  publicWebinar?: PublicEWebinarFragment;
  formSessionType: FormSessionType;
  formTitle?: string;
  showGdprBanner?: boolean;
  gdprBannerText?: string;
}

export interface ThankYouWidgetConfig {
  onlyButton: boolean;
  jitBtnText: string;
  replayBtnText: string;
  addToCalendarBtnText: string;
  thankYouData?: ThankYouData;
}

export enum ButtonAlign {
  Left = 'Left',
  Center = 'Center',
  Right = 'Right',
}

export interface ButtonWidgetConfig {
  btnText: string;
  showButtonTimer: boolean;
  buttonTextPortalId?: string;
  buttonPrimaryColor?: string;
  buttonReadableColor?: string;
  font?: string;
  fontCssUrl?: string;
  fontSize?: number;
  isFullWidth?: boolean;
  align?: ButtonAlign;
}

export interface FloatInWidgetConfig {
  collapsed?: boolean;
  position: WidgetPosition;
  mobilePosition: WidgetPosition;
}

export interface FixedBarWidgetConfig {
  showTitle?: boolean;
}

export interface CardWidgetConfig {
  image: string;
  video?: string;
  description: string;
  presenters: Presenter[];
}

export interface RegisterFormWidgetConfig {
  hideSessionsDropdown: boolean;
  showOnlyBuiltInFields: boolean;
  openLinkInNewWindow: boolean;
  showConsentCheckbox: boolean;
  showFieldsLabel: boolean;
  horizontal: boolean;
  consentCheckboxText: string;
  formSessionType: FormSessionType;
  fields?: RegistrationFormField[];
}
export interface WebinarWidgetConfig {
  primaryColor: string;
  readableColor: string;
  actionColor: string;
  readableActionColor: string;
  readableOnWhiteColor: string;
  language: string;
  borderRadius: number;
  title: string;
}

export interface EmbeddedPlayerConfig {
  sessionType: UngatedSessionType;
  thumbnail: string;
  disabled?: boolean;
  playButtonCTAText?: string;
  playButtonCTATextColor?: string;
  playButtonBackgroundBlur?: number;
  playButtonBackgroundColor?: string;
}

export interface WidgetConfig {
  root?: string;
  url: string;
  shortUrl: string;
  source?: string;
  isReview?: boolean;
  isWebinarRegPage?: boolean;
  openInPopup: boolean;
  isMobile?: boolean;
  sessions: Session[];
  mode?: PageMode;
  for?: WidgetFor;
  type: WidgetType;
  ewebinar: WebinarWidgetConfig;
  button?: ButtonWidgetConfig;
  floatIn?: FloatInWidgetConfig;
  fixedBar?: FixedBarWidgetConfig;
  thankyou?: ThankYouWidgetConfig;
  card?: CardWidgetConfig;
  registerForm?: RegisterFormWidgetConfig;
  embeddedPlayer?: EmbeddedPlayerConfig;
  serverDateNow?: ServerDateNow;
  showGdprBanner?: boolean | undefined; // Legacy
  gdprBannerMode?: CookieBannerMode;
  gdprBannerText?: string;
  hideBranding?: boolean | null;
  teamId?: string;
  carouselId?: string;
  formType?: RegistrationFormType;
  initialized?: boolean; // whether or not the Widget initialized
}

export interface GdprBannerConfig extends Partial<WidgetConfig> {
  mode?: PageMode;
  type: WidgetType;
  showGdprBanner?: boolean;
  gdprBannerMode?: CookieBannerMode;
  gdprBannerText?: string;
}

export interface PresenterCarouselConfig extends Partial<WidgetConfig> {
  mode?: PageMode;
  type: WidgetType;
  carouselId?: string;
}

export type DefaultWidgetConfig = Omit<
  Required<WidgetConfig>,
  'publicWebinar' | 'serverDateNow' | 'formUrl'
> & {
  publicWebinar?: PublicEWebinarFragment;
};

export interface ThankYouData {
  sessionText: string;
  primaryColor: string;
  readableColor: string;
  actionColor: string;
  readableActionColor: string;
  readableOnWhiteColor: string;
  borderRadius: number;
  session: Date;
  isReplay: boolean;
  attendeeId: string;
  joinLink: string;
  language: string;
}

export interface RegistrantApiResponse {
  attendee: {
    id: string;
    visitorId: string;
    email: string;
    startTime: string;
    isOnDemand: boolean;
    firstName?: string | null;
    lastName?: string | null;
    watchingReplay?: boolean | null;
    urls: AttendeeUrls;
    sessionType?: SessionType | null;
    utmParams?: UtmParamsFragment;
  };
  ewebinar: Pick<
    PublicEWebinarFragment,
    'scheduleSettings' | 'thankyouPageSettings' | 'viewerRoomSettings'
  >;
  pixelID?: string;
}

export enum WidgetEventType {
  PageDataLoaded = 'PageDataLoaded',
  IFrameReady = 'IFrameReady',
  Register = 'Register',
  FormOpen = 'FormOpen',
  FormClose = 'FormClose',
  HasNoSession = 'HasNoSession',
  UpdateSession = 'UpdateSession',
  UpdateSessions = 'UpdateSessions',
  TrackPageView = 'TrackPageView',
  AcceptedCookies = 'AcceptedCookies',
  IFrameAcceptedCookies = 'IFrameAcceptedCookies',
  PlayerUpdateInfo = 'PlayerUpdateInfo',
  PlayerToggleExpand = 'PlayerToggleExpand',
  PlayerReadyToJoin = 'PlayerReadyToJoin',
  PlayerHadComeToExitRoom = 'PlayerHadComeToExitRoom',
  UnSupportedBrowser = 'UnSupportedBrowser',
  PlayerJoined = 'PlayerJoined',
}

export interface FormOpenData {
  session?: string;
  previewData?: {
    sessions: Session[];
  };
  widgetInfo: WidgetInfo;
}

export interface EmbeddedPlayerInfo extends EmbeddedPlayerConfig {
  width: number;
  height: number;
  href?: string;
  referrer?: string;
  playButtonCTAFontSize?: number;
  visitorId?: string;
}

export type WidgetEvent =
  | {
      event: WidgetEventType.Register;
      data: RegisterData;
    }
  | {
      event: WidgetEventType.FormOpen;
      data: FormOpenData;
    }
  | {
      event: WidgetEventType.FormClose;
    }
  | {
      event: WidgetEventType.UpdateSession;
      session?: string;
    }
  | {
      event: WidgetEventType.HasNoSession;
    }
  | {
      event: WidgetEventType.IFrameReady;
      data?: {
        ewebinar: PublicEWebinarFragment;
      };
    }
  | {
      event: WidgetEventType.TrackPageView;
    }
  | {
      event: WidgetEventType.AcceptedCookies;
    }
  | {
      event: WidgetEventType.IFrameAcceptedCookies;
    }
  | {
      event: WidgetEventType.PlayerToggleExpand;
      expand: boolean;
    }
  | {
      event: WidgetEventType.PlayerReadyToJoin;
      aspectRatio: number;
    }
  | {
      event: WidgetEventType.PlayerHadComeToExitRoom;
    }
  | {
      event: WidgetEventType.UnSupportedBrowser;
    }
  | {
      event: WidgetEventType.PlayerJoined;
    }
  | {
      event: WidgetEventType.PlayerUpdateInfo;
      data: EmbeddedPlayerInfo;
    };

export type WidgetEventListener = (event: WidgetEvent) => void;

export type RegisterAttendeeRestAPIInput = {
  ewebinarId?: string | null | undefined;
  firstName?: string | null | undefined;
  lastName?: string | null | undefined;
  email?: string | null | undefined;
  timezone?: string | null | undefined;
  session?: string | null | undefined;
  sessionType?: string | null | undefined;
  source?: string | null | undefined;
  referrer?: string | null | undefined;
  origin?: string | null | undefined;
  attendeeFields?: AttendeeField[] | null | undefined;
  visitorId?: string | null | undefined;
} & {
  [key: string]: string | null | undefined;
};
